import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import type { FormNode } from '../../../../types/form.types'
import {
  isListButtonDisabled,
  resolveListButtonClick,
  type ListFormRuntimeActions,
} from '@/shared/FormRenderer/listFormActions'
import {
  ButtonGraphic,
  buttonLabel,
  buttonShowsGraphic,
  buttonShowsText,
} from '../utils/buttonContent'
import { confirmAsync } from '@/shared/dialogs'

export type ButtonRuntimeAction = 'submit' | 'save' | 'close'

export interface ButtonElementProps {
  node: FormNode
  preview?: boolean
  runtimeActions?: {
    resolveAction: (node: FormNode) => ButtonRuntimeAction | undefined
    submit: () => void
    save?: () => void
    close: () => void
    disabled?: boolean
  }
  listActions?: ListFormRuntimeActions
}

export function ButtonElement({ node, preview, runtimeActions, listActions }: ButtonElementProps) {
  const listHandler = listActions ? resolveListButtonClick(node, listActions) : undefined
  const dialogAction = runtimeActions?.resolveAction(node)
  const inRuntime = !!listActions || !!runtimeActions
  const hasHandler = !!listHandler || !!dialogAction

  const disabled = inRuntime
    ? listActions
      ? isListButtonDisabled(node, listActions) || !listHandler
      : dialogAction
        ? (runtimeActions!.disabled ?? false)
        : true
    : !preview

  const onClick = async () => {
    if (node.confirmMessage && !(await confirmAsync(node.confirmMessage))) return
    if (listHandler) {
      listHandler()
      return
    }
    if (!runtimeActions || !dialogAction) return
    if (dialogAction === 'submit') runtimeActions.submit()
    else if (dialogAction === 'save') runtimeActions.save?.()
    else runtimeActions.close()
  }

  // Theme default is contained (operator MuiButton). Explicit node.buttonVariant wins.
  const variant = node.buttonVariant ?? 'contained'

  // Resolve color
  const color = node.buttonColor ?? 'primary'

  // Resolve size
  const size = node.buttonSize ?? 'small'

  const label = buttonLabel(node)
  const showText = buttonShowsText(node)
  const showGraphic = buttonShowsGraphic(node)
  const graphic = showGraphic ? <ButtonGraphic node={node} size={size === 'large' ? 22 : size === 'medium' ? 20 : 18} /> : null

  const btn = (
    <Button
      type="button"
      size={size}
      variant={variant}
      color={color}
      disableElevation
      tabIndex={-1}
      disabled={disabled}
      onClick={inRuntime && hasHandler ? onClick : undefined}
      startIcon={showGraphic && graphic ? graphic : undefined}
      aria-label={!showText && label ? label : undefined}
      sx={{
        textTransform: 'none',
        fontSize: node.style?.fontSize ?? 13,
        fontWeight: node.style?.bold ? 700 : undefined,
        fontStyle: node.style?.italic ? 'italic' : undefined,
        ...(node.style?.bgColor ? { bgcolor: node.style.bgColor } : {}),
        ...(node.style?.textColor ? { color: node.style.textColor } : {}),
        ...(node.style?.borderColor ? { borderColor: node.style.borderColor } : {}),
        '& .MuiButton-startIcon': showGraphic && !showText ? { margin: 0 } : undefined,
      }}
    >
      {showText ? label : null}
    </Button>
  )

  if (node.tooltip) {
    return <Tooltip title={node.tooltip}>{btn}</Tooltip>
  }

  return btn
}
